import { z } from 'zod';
import { moneySchema } from '../money';

/**
 * Lagercockpit (Modul Artikel & Lager, Unterseite „Lagercockpit"): eine
 * Dispositions-Sicht für den Einkauf — was muss heute bestellt werden, was
 * kommt rein, was wird knapp. Der Server rechnet alles fertig aus; das Frontend
 * zeigt nur an (§9: keine Geschäftslogik in Komponenten). Geld als Money
 * (bigint-Mikro als String, §6); Mengen als Float wie im Bestand (ADR 0006).
 *
 * „Leer" wird auf zwei Uhren gemessen (Entscheid Tech-Lead 2026-09-07), die
 * frühere gewinnt:
 *  1. Verbrauch: frei ÷ Ø Tagesverbrauch der letzten 90 Tage (Abgänge im Ledger)
 *  2. Bekannter Bedarf: offener Projekt-Materialbedarf (Termin = Projekt-
 *     Liefertermin minus Vorlauf, wie die Bedarfsvorschau-Ableitung, ADR 0086)
 *     und Bedarfsvorschau-Zeilen, nach Datum aufgereiht gegen den freien Bestand.
 *
 * Ist am Artikel eine Wiederbeschaffungszeit gepflegt, rechnet das Cockpit daraus
 * die **Bestellfrist** (2026-09-08): Leer-Datum − Wiederbeschaffungszeit = der Tag,
 * an dem spätestens bestellt sein muss. Erst das macht aus „leer am" ein
 * „bestellen bis" — und holt Artikel nach vorne, deren Frist heute schon läuft,
 * obwohl der Bestand noch über dem Meldebestand steht.
 */

/** Horizonte, die die Oberfläche anbietet (Tage). */
export const INVENTORY_COCKPIT_HORIZONS = [14, 30, 60, 90] as const;
export const INVENTORY_COCKPIT_DEFAULT_HORIZON = 30;
/** Fenster für den Ø-Tagesverbrauch (Uhr 1). */
export const INVENTORY_COCKPIT_CONSUMPTION_DAYS = 90;
/** Ohne Abgang seit so vielen Tagen gilt ein Artikel mit Bestand als Ladenhüter (wie `stock-coverage`). */
export const INVENTORY_COCKPIT_DEAD_STOCK_DAYS = 365;
/** Zulauf-Vorschau in der rechten Spalte (Tage). */
export const INVENTORY_COCKPIT_INBOUND_DAYS = 14;
/** Wochen im Zu-/Abgangs-Diagramm. */
export const INVENTORY_COCKPIT_WEEKS = 12;

export const inventoryCockpitQuerySchema = z.object({
  horizonDays: z.coerce.number().int().min(7).max(180).default(INVENTORY_COCKPIT_DEFAULT_HORIZON),
  /**
   * Hauptwarengruppe (1000er-Band der 4-stelligen Warengruppe, ADR 0016), optional.
   * Das Cockpit filtert bewusst grob: Der Einkauf disponiert je Materialfamilie
   * (Rohre, Armaturen …), nicht je 4-stelliger Untergruppe.
   */
  commodityMainGroup: z.coerce.number().int().min(0).max(9000).multipleOf(1000).optional(),
});
export type InventoryCockpitQuery = z.infer<typeof inventoryCockpitQuerySchema>;

/**
 * Handlungs-Bucket je Lagerartikel:
 * - ORDER: unter Meldebestand oder ohne freien Bestand ODER Bestellfrist erreicht
 *   (Leer-Datum − Wiederbeschaffungszeit ist heute oder vorbei) — und kein Zulauf rettet
 * - SOON: läuft im Horizont leer oder die Bestellfrist fällt in den Horizont,
 *   der Bestand ist aber noch über dem Meldebestand
 * - COVERED: Zulauf kommt vor dem Leer-Datum und hebt über den Meldebestand
 * - OK: alles andere
 */
export const cockpitBucketSchema = z.enum(['ORDER', 'SOON', 'COVERED', 'OK']);
export type CockpitBucket = z.infer<typeof cockpitBucketSchema>;

/** Welche Uhr das Leer-Datum bestimmt hat. */
export const cockpitEmptyReasonSchema = z.enum([
  'NO_STOCK',
  'ALL_RESERVED',
  'CONSUMPTION',
  'PROJECT_DEMAND',
  'FORECAST',
]);
export type CockpitEmptyReason = z.infer<typeof cockpitEmptyReasonSchema>;

/** Ampel wie `StockLevelDot` (frei vs. Meldebestand). */
export const cockpitStockLevelSchema = z.enum(['red', 'amber', 'green']);
export type CockpitStockLevel = z.infer<typeof cockpitStockLevelSchema>;

/** Ein terminierter Bedarf (Uhr 2). */
export const cockpitDemandSchema = z.object({
  kind: z.enum(['PROJECT', 'FORECAST']),
  quantity: z.number(),
  /** Bedarfstermin; null = ohne Termin (zählt als sofort). */
  neededOn: z.string().datetime().nullable(),
  /** Tage ab heute (0 = heute/überfällig/ohne Termin). */
  inDays: z.number().int(),
  projectId: z.string().nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  forecastId: z.string().nullable(),
  forecastName: z.string().nullable(),
});
export type CockpitDemand = z.infer<typeof cockpitDemandSchema>;

export const cockpitRowSchema = z.object({
  articleId: z.string(),
  number: z.string(),
  name: z.string(),
  unit: z.string(),
  commodityGroup: z.number().int().nullable(),
  mainGroupCode: z.number().int().nullable(),
  supplierId: z.string().nullable(),
  supplierName: z.string().nullable(),

  stockQty: z.number(),
  reservedQty: z.number(),
  /** Bestand − aktive Reservierungen. */
  freeQty: z.number(),
  minStock: z.number().nullable(),
  maxStock: z.number().nullable(),
  level: cockpitStockLevelSchema,

  /** Offener Zulauf aus Bestellungen (Entwurf/Bestellt, nicht Pool — ADR 0041). */
  inboundQty: z.number(),
  inboundNextDate: z.string().datetime().nullable(),
  /** Tage bis zur frühesten Lieferung; negativ = überfällig; null = kein Zulauf/kein Termin. */
  inboundInDays: z.number().int().nullable(),
  inboundOrderId: z.string().nullable(),
  inboundOrderNumber: z.string().nullable(),
  isInboundOverdue: z.boolean(),

  /** Uhr 1 */
  consumedQty: z.number(),
  consumptionWindowDays: z.number().int(),
  dailyConsumption: z.number(),
  /** Tage, bis der freie Bestand durch Verbrauch aufgebraucht ist; null = kein Verbrauch. */
  daysByConsumption: z.number().int().nullable(),

  /** Uhr 2 */
  demands: z.array(cockpitDemandSchema),
  /** Tage, bis der freie Bestand durch terminierten Bedarf unter null fällt; null = reicht. */
  daysByDemand: z.number().int().nullable(),

  /** Ergebnis: frühere Uhr. null = reicht über den Horizont hinaus. */
  emptyInDays: z.number().int().nullable(),
  emptyOn: z.string().datetime().nullable(),
  emptyReason: cockpitEmptyReasonSchema.nullable(),

  /** Wiederbeschaffungszeit des Artikels in Tagen; null = nicht gepflegt. */
  leadTimeDays: z.number().int().nullable(),
  /**
   * Bestellfrist: Tage bis zu dem Tag, an dem spätestens bestellt sein muss,
   * damit die Ware vor dem Leer-Datum da ist (Leer-Datum − Wiederbeschaffungszeit).
   * Negativ = Frist überschritten. null = keine Wiederbeschaffungszeit oder kein
   * Leer-Datum, dann gibt es keine Frist.
   */
  orderByInDays: z.number().int().nullable(),
  orderByOn: z.string().datetime().nullable(),

  demandInHorizonQty: z.number(),
  consumptionInHorizonQty: z.number(),
  /** Kalkuliert: frei + Zulauf. */
  projectedQty: z.number(),
  /** Vorschlagsmenge (auf die EK-Staffel aufgerundet, wenn vorhanden). 0 = kein Vorschlag. */
  suggestedQty: z.number(),
  /** Kleinste EK-Staffelmenge des Artikels; null = keine Staffel. */
  tierQty: z.number().nullable(),
  /** EK (Ø-EK, sonst Standard-EK); null ohne `inventory.viewprices` oder ohne Preis. */
  purchasePrice: moneySchema.nullable(),
  suggestedValue: moneySchema.nullable(),

  isCoveredByInbound: z.boolean(),
  bucket: cockpitBucketSchema,
});
export type CockpitRow = z.infer<typeof cockpitRowSchema>;

export const cockpitInboundOrderSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  supplierName: z.string(),
  expectedDate: z.string().datetime().nullable(),
  /** Tage ab heute; negativ = überfällig; null = ohne Termin. */
  inDays: z.number().int().nullable(),
  itemCount: z.number().int(),
  isOverdue: z.boolean(),
});
export type CockpitInboundOrder = z.infer<typeof cockpitInboundOrderSchema>;

export const cockpitProjectAtRiskSchema = z.object({
  projectId: z.string(),
  projectCode: z.string(),
  projectName: z.string(),
  itemCount: z.number().int(),
});
export type CockpitProjectAtRisk = z.infer<typeof cockpitProjectAtRiskSchema>;

export const cockpitTilesSchema = z.object({
  order: z.object({
    count: z.number().int(),
    withoutFreeStock: z.number().int(),
    /** Davon nur wegen der erreichten Bestellfrist (noch über dem Meldebestand). */
    byDeadline: z.number().int(),
    suggestedValue: moneySchema.nullable(),
  }),
  soon: z.object({
    count: z.number().int(),
    byConsumption: z.number().int(),
    byDemand: z.number().int(),
  }),
  overdue: z.object({
    orderCount: z.number().int(),
    itemCount: z.number().int(),
    first: cockpitInboundOrderSchema.nullable(),
  }),
  risk: z.object({
    projectCount: z.number().int(),
    itemCount: z.number().int(),
    projects: z.array(cockpitProjectAtRiskSchema),
  }),
});
export type CockpitTiles = z.infer<typeof cockpitTilesSchema>;

export const cockpitSummarySchema = z.object({
  articleCount: z.number().int(),
  belowMinCount: z.number().int(),
  /** Bestand × EK über alle Lagerartikel im Filter; null ohne Preisrecht. */
  stockValue: moneySchema.nullable(),
  deadStockCount: z.number().int(),
  deadStockValue: moneySchema.nullable(),
});
export type CockpitSummary = z.infer<typeof cockpitSummarySchema>;

export const cockpitMovementSchema = z.object({
  id: z.string(),
  type: z.enum(['IN', 'OUT', 'ADJUST']),
  articleId: z.string(),
  articleNumber: z.string(),
  articleName: z.string(),
  quantity: z.number(),
  occurredAt: z.string().datetime(),
  sourceType: z.string().nullable(),
  sourceRef: z.string().nullable(),
  projectCode: z.string().nullable(),
});
export type CockpitMovement = z.infer<typeof cockpitMovementSchema>;

export const cockpitTodaySchema = z.object({
  inQty: z.number(),
  inCount: z.number().int(),
  outQty: z.number(),
  outCount: z.number().int(),
  entries: z.array(cockpitMovementSchema),
});
export type CockpitToday = z.infer<typeof cockpitTodaySchema>;

export const cockpitWeekSchema = z.object({
  /** Montag der Woche (ISO-Datum YYYY-MM-DD). */
  weekStart: z.string(),
  /** „KW 35" */
  label: z.string(),
  inQty: z.number(),
  outQty: z.number(),
});
export type CockpitWeek = z.infer<typeof cockpitWeekSchema>;

export const cockpitDeadStockSchema = z.object({
  articleId: z.string(),
  number: z.string(),
  name: z.string(),
  unit: z.string(),
  stockQty: z.number(),
  boundCapital: moneySchema.nullable(),
});
export type CockpitDeadStock = z.infer<typeof cockpitDeadStockSchema>;

export const inventoryCockpitSchema = z.object({
  generatedAt: z.string().datetime(),
  horizonDays: z.number().int(),
  commodityMainGroup: z.number().int().nullable(),
  /** Vorlauf (Wochen), mit dem Projekt-Bedarf vor dem Liefertermin terminiert wird. */
  demandLeadTimeWeeks: z.number().int(),
  tiles: cockpitTilesSchema,
  summary: cockpitSummarySchema,
  rows: z.array(cockpitRowSchema),
  inbound: z.array(cockpitInboundOrderSchema),
  today: cockpitTodaySchema,
  weekly: z.array(cockpitWeekSchema),
  deadStockTop: z.array(cockpitDeadStockSchema),
});
export type InventoryCockpit = z.infer<typeof inventoryCockpitSchema>;
